import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@components/ui/accordion";
import { Trash2 } from "lucide-react";
import FallBackUi from "./FallBackUi";

interface MappingGroup {
  uid: string;
  title: string;
  subtitle?: string;
  fields: any[];
}

interface ViewMappingsDialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  mappingGroups: MappingGroup[];
  onDeleteGroup: (title: string, logUid?: string) => void;
}

const ViewMappingsDialog: React.FC<ViewMappingsDialogProps> = ({
  open,
  onClose,
  title,
  mappingGroups = [],
  onDeleteGroup,
}) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[60%] max-h-[80vh] overflow-auto flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {mappingGroups.length === 0 ? (
          <FallBackUi
            title={`No ${title === "Data List" ? "datalists" : "mappings"} found !`}
          />
        ) : (
          <Accordion type="multiple" className="w-full space-y-3">
            {mappingGroups.map((group) => (
              <AccordionItem
                key={group.uid}
                value={group.uid}
                className="border rounded-md bg-muted"
              >
                {/* HEADER */}
                <AccordionTrigger className="px-4 py-3 hover:no-underline">
                  <div className="flex w-full items-center justify-between gap-6 ">
                    {/* LEFT: Title */}
                    <div className="flex flex-col text-left">
                      <p className="text-sm">
                        {" "}
                        Title: <strong>{group.title}</strong>
                      </p>
                      {group.subtitle && (
                        <p className="text-sm text-muted-foreground">
                          {group.subtitle}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-4 pr-4">
                      <Trash2
                        className="h-4 w-4 text-muted-foreground hover:text-destructive cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteGroup(group.title, group.uid);
                        }}
                      />
                    </div>
                  </div>
                </AccordionTrigger>

                {/* CONTENT */}
                <AccordionContent className="border m-2 px-2 rounded-md bg-background">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Raw Field</TableHead>
                        <TableHead>Mapped Field</TableHead>
                        <TableHead>Display Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Category</TableHead>
                      </TableRow>
                    </TableHeader>

                    <TableBody>
                      {group.fields?.length ? (
                        group.fields.map((field: any, idx: number) => (
                          <TableRow key={idx}>
                            <TableCell>{field.rawFieldName}</TableCell>
                            <TableCell>{field.mappingLsField || "-"}</TableCell>
                            <TableCell>{field.display_name || "-"}</TableCell>
                            <TableCell>
                              {field.type || field.data_type || "-"}
                            </TableCell>
                            <TableCell>
                              {field.category || field.column_type || "-"}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            className="text-center text-muted-foreground"
                          >
                            No mapped fields
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewMappingsDialog;
